/**
 * Plan entitlement catalog — mirrors backend/src/lib/planEntitlements.js
 */

export type EntitlementItem = { id: string; label: string };

export type EntitlementGroup = {
  module: string;
  label: string;
  items: EntitlementItem[];
};

export const ENTITLEMENT_CATALOG: EntitlementGroup[] = [
  {
    module: 'dashboard',
    label: 'Dashboard overview',
    items: [
      { id: 'dashboard.widget.total_users', label: 'Total active users' },
      { id: 'dashboard.widget.collected_emis', label: 'Collected EMIs' },
      { id: 'dashboard.widget.closed_hps', label: 'Closed HPs' },
      { id: 'dashboard.widget.total_loan', label: 'Total loan amount' },
      { id: 'dashboard.widget.vehicles_available', label: 'Vehicles available' },
      { id: 'dashboard.widget.vehicles_sold', label: 'Vehicles sold' },
      { id: 'dashboard.widget.hand_loans', label: 'Hand loans summary' },
      { id: 'dashboard.widget.seized_hps', label: 'Seized HPs' },
      { id: 'dashboard.cash_bank.flows', label: 'Cash & bank flow KPIs' },
      { id: 'dashboard.chart.collections_trend', label: 'Collections trend chart' },
      { id: 'dashboard.chart.portfolio_split', label: 'Portfolio split chart' },
      { id: 'dashboard.chart.top_borrowers', label: 'Top borrowers chart' },
      { id: 'dashboard.actions.quick_origination', label: 'Quick origination shortcuts' },
    ],
  },
  {
    module: 'dashboard',
    label: 'Accounting master',
    items: [
      { id: 'master.kpi.finance_income', label: 'Finance income KPI' },
      { id: 'master.kpi.consultancy_profit', label: 'Consultancy profit KPI' },
      { id: 'master.kpi.expenses', label: 'Expenses KPI' },
      { id: 'master.kpi.net_accounting', label: 'Net accounting KPI' },
      { id: 'master.tabs.combined', label: 'Combined accounting tab' },
      { id: 'master.export.summary', label: 'Accounting export' },
      { id: 'master.migration.opening_books', label: 'Opening books & cutover balances' },
      { id: 'master.migration.facilities', label: 'Migrate finance & hand loans' },
    ],
  },
  {
    module: 'finance',
    label: 'Vehicle finance',
    items: [
      { id: 'finance.origination.new_hp', label: 'New HP origination' },
      { id: 'finance.collections.emi_ledger', label: 'EMI ledger & collections' },
      { id: 'finance.collections.record_payment', label: 'Record EMI / cash collection' },
      { id: 'finance.refinance', label: 'Refinance facility' },
      { id: 'finance.migration.cutover', label: 'Migrate existing HP at cutover' },
      { id: 'finance.documents.loan_pdf', label: 'Loan application PDF' },
      { id: 'finance.export.schedule', label: 'Installment schedule export' },
      { id: 'finance.seized.tracking', label: 'Seized vehicle tracking' },
      { id: 'finance.closure.close_file', label: 'Close facility file' },
    ],
  },
  {
    module: 'loans',
    label: 'Hand loans',
    items: [
      { id: 'loans.records.crud', label: 'Hand loan records' },
      { id: 'loans.collections.emi', label: 'EMI repayment & collections' },
      { id: 'loans.interest.ledger', label: 'Daily / simple / compound interest ledger' },
      { id: 'loans.migration.cutover', label: 'Migrate hand loan at cutover' },
      { id: 'loans.overview.widget', label: 'Hand loans dashboard widget' },
    ],
  },
  {
    module: 'customers',
    label: 'Customers',
    items: [
      { id: 'customers.unified_list', label: 'Unified borrower list' },
      { id: 'customers.profile.details', label: 'Customer profile & facilities' },
      { id: 'customers.collections.emi', label: 'Collect EMI from customer profile' },
      { id: 'customers.export.data', label: 'Customer list & profile exports' },
      { id: 'customers.share.whatsapp', label: 'Share report PDF via WhatsApp' },
      { id: 'customers.portal.provision', label: 'Customer portal (borrower login)' },
      { id: 'customers.sms.reminders', label: 'SMS payment reminders' },
    ],
  },
  {
    module: 'consultancy',
    label: 'Consultancy',
    items: [
      { id: 'consultancy.inventory.vehicles', label: 'Vehicle inventory' },
      { id: 'consultancy.sales.desk', label: 'Sales desk' },
      { id: 'consultancy.stock.operations', label: 'Buy / sell stock operations' },
      { id: 'consultancy.investment.tracking', label: 'Investment tracking' },
      { id: 'consultancy.revenue.reporting', label: 'Revenue reporting' },
      { id: 'consultancy.profit_loss', label: 'Profit & loss desk' },
      { id: 'consultancy.transaction.ledger', label: 'Sales transaction ledger' },
    ],
  },
  {
    module: 'employees',
    label: 'Employees',
    items: [
      { id: 'employees.records', label: 'Employee records' },
      { id: 'employees.payroll.export', label: 'Payroll report export' },
    ],
  },
  {
    module: 'expenses',
    label: 'Expenses',
    items: [
      { id: 'expenses.records', label: 'Expense tracking' },
      { id: 'expenses.report.export', label: 'Expense report export' },
    ],
  },
  {
    module: 'reports',
    label: 'Reports',
    items: [
      { id: 'reports.day_report', label: 'Day report' },
      { id: 'reports.collections.overdue', label: 'Collections & overdue sections' },
      { id: 'reports.export.pdf_excel', label: 'PDF & Excel exports' },
      { id: 'reports.share.whatsapp', label: 'WhatsApp share on reports' },
    ],
  },
  {
    module: 'audit',
    label: 'Audit & compliance',
    items: [
      { id: 'audit.logs.view', label: 'Audit trail viewer' },
      { id: 'audit.logs.export', label: 'Audit log export' },
      { id: 'audit.reports.generate', label: 'Audit report generation' },
    ],
  },
  {
    module: 'audit',
    label: 'Finance ERP suite',
    items: [
      { id: 'audit.erp.suite', label: 'ERP workspace access' },
      { id: 'audit.erp.financial_year', label: 'Financial year open / close' },
      { id: 'audit.erp.statements', label: 'Trial balance, P&L, balance sheet & cashflow' },
      { id: 'audit.erp.books', label: 'Day book, ledger & journal' },
      { id: 'audit.erp.reconciliation', label: 'Cash / bank reconciliation' },
      { id: 'audit.erp.ai_auditor', label: 'AI auditor insights' },
      { id: 'audit.erp.report_pack', label: 'Combined audit report export' },
    ],
  },
  {
    module: 'settings',
    label: 'Settings',
    items: [
      { id: 'settings.company.branding', label: 'Company branding & PDF letterhead' },
      { id: 'settings.admins.workspace', label: 'Workspace admins & roles' },
      { id: 'settings.ledgers.multi_branch', label: 'Multi-ledger branch books' },
      { id: 'settings.ledgers.desk_login', label: 'Branch desk login credentials' },
      { id: 'settings.banks.lines', label: 'Banks & service lines' },
      { id: 'settings.owners.cap_table', label: 'Owners & shareholding register' },
      { id: 'settings.finance.defaults', label: 'Default interest & penalty rates' },
      { id: 'settings.payment.tracking', label: 'Payment mode preferences' },
      { id: 'settings.subscription.self_serve', label: 'Self-serve subscription' },
      { id: 'settings.integrations.sms', label: 'SMS gateway & reminder schedule' },
      { id: 'settings.integrations.google', label: 'Google Sign-In' },
      { id: 'settings.language.i18n', label: 'Multi-language UI (English, Hindi, Telugu)' },
      { id: 'settings.appearance.theme', label: 'Theme customization' },
      { id: 'settings.appearance.font', label: 'Font customization' },
    ],
  },
];

export const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  finance: 'Finance',
  loans: 'Hand loans',
  customers: 'Customers',
  consultancy: 'Consultancy',
  employees: 'Employees',
  expenses: 'Expenses',
  reports: 'Reports',
  audit: 'Audit logs',
  settings: 'Settings',
};

/** SMS items controlled by the dedicated plan toggle — hidden from granular checklist. */
export const SMS_PLAN_ITEM_IDS = new Set([
  'customers.sms.reminders',
  'settings.integrations.sms',
]);

export function emptyFeatureItems(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  ENTITLEMENT_CATALOG.forEach((g) => g.items.forEach((i) => { out[i.id] = false; }));
  return out;
}

/** Plan feature gate for automated/manual SMS payment reminders. */
export const SMS_REMINDER_FEATURE_ID = 'customers.sms.reminders';
export const SMS_INTEGRATION_FEATURE_ID = 'settings.integrations.sms';

export function itemModule(itemId: string): string | null {
  if (itemId.startsWith('master.')) return 'dashboard';
  const top = itemId.split('.')[0];
  return top in MODULE_LABELS ? top : null;
}

export function planItemLabels(plan: { features?: Record<string, boolean>; featureItems?: Record<string, boolean> }) {
  const labels: string[] = [];
  ENTITLEMENT_CATALOG.forEach((g) => {
    if (!plan.features?.[g.module]) return;
    const items = g.items.filter((i) => {
      if (SMS_PLAN_ITEM_IDS.has(i.id)) return Boolean(plan.featureItems?.[i.id]);
      const configured = Object.values(plan.featureItems || {}).some(Boolean);
      if (!configured) return true;
      return Boolean(plan.featureItems?.[i.id]);
    });
    if (items.length) labels.push(...items.map((i) => i.label));
  });
  return labels;
}

/** Counts for the super-admin plan builder UI. */
export function entitlementCatalogStats() {
  const moduleCount = Object.keys(MODULE_LABELS).length;
  const groupCount = ENTITLEMENT_CATALOG.length;
  const itemCount = ENTITLEMENT_CATALOG.reduce((n, g) => n + g.items.length, 0);
  const checklistCount = ENTITLEMENT_CATALOG.reduce(
    (n, g) => n + g.items.filter((i) => !SMS_PLAN_ITEM_IDS.has(i.id)).length,
    0
  );
  return { moduleCount, groupCount, itemCount, checklistCount, smsItemCount: SMS_PLAN_ITEM_IDS.size };
}

/** Enabled module names + granular capability labels for plan summaries. */
export function planCapabilitySummary(plan: {
  features?: Record<string, boolean>;
  featureItems?: Record<string, boolean>;
}) {
  const modules = Object.keys(MODULE_LABELS).filter((m) => plan.features?.[m]).map((m) => MODULE_LABELS[m]);
  const hasGranular = Object.entries(plan.featureItems || {}).some(
    ([id, on]) => on && !SMS_PLAN_ITEM_IDS.has(id)
  );
  const capabilities: { group: string; label: string }[] = [];
  ENTITLEMENT_CATALOG.forEach((g) => {
    if (!plan.features?.[g.module]) return;
    g.items.forEach((item) => {
      if (SMS_PLAN_ITEM_IDS.has(item.id)) {
        if (plan.featureItems?.[item.id]) capabilities.push({ group: g.label, label: item.label });
        return;
      }
      if (!hasGranular || plan.featureItems?.[item.id]) {
        capabilities.push({ group: g.label, label: item.label });
      }
    });
  });
  return { modules, capabilities, hasGranular };
}
